import { motion } from "framer-motion";
import { STICKY } from "../data/modules";

export const Mark = ({ text }) => {
  const parts = String(text).split(/==(.+?)==/g);
  return parts.map((p, i) => (i % 2 ? <span key={i} className="hl">{p}</span> : <span key={i}>{p}</span>));
};

export const Reveal = ({ children, delay = 0, className = "", y = 24 }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.12 }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

export const Sticky = ({ color = "yellow", rotate = 0, children, className = "", testId }) => (
  <div className={`sticky-note ${className}`} style={{ background: STICKY[color], "--r": `${rotate}deg` }} data-testid={testId}>
    <span className="tape" aria-hidden />
    {children}
  </div>
);

export const Example = ({ ex, color = "yellow", rotate = -0.6, testId }) => (
  <Sticky color={color} rotate={rotate} className="my-8 max-w-2xl" testId={testId}>
    <p className="font-hand text-xl text-[#E4572E] mb-3">e.g. {ex.title}</p>
    <ul className="space-y-2">
      {ex.lines.map((l) => (
        <li key={l} className="flex gap-3 text-[15px] leading-relaxed"><span className="font-hand text-lg leading-6">→</span><span>{l}</span></li>
      ))}
    </ul>
  </Sticky>
);

export const Hinglish = ({ text }) => (
  <p className="font-hand text-xl sm:text-2xl text-[#2B59C3] my-6 pl-5 border-l-4 border-[#2B59C3]/40 leading-snug" data-testid="hinglish-note">
    <span className="text-[#E4572E]">Samjho aise: </span>{text}
  </p>
);

export const Points = ({ items }) => (
  <ul className="my-6 space-y-3">
    {items.map((t) => (
      <li key={t} className="flex gap-3 leading-relaxed"><span className="tick" aria-hidden>✓</span><span><Mark text={t} /></span></li>
    ))}
  </ul>
);

export const Steps = ({ items }) => (
  <ol className="my-8 grid gap-4">
    {items.map((s, i) => (
      <li key={s.t} className="step">
        <span className="step-num">{i + 1}</span>
        <div><p className="font-semibold text-[17px]">{s.t}</p><p className="text-[15px] text-[#4A4A55] leading-relaxed mt-1">{s.d}</p></div>
      </li>
    ))}
  </ol>
);

export const Chips = ({ items }) => (
  <div className="flex flex-wrap gap-2 my-6">
    {items.map((c, i) => <span key={c} className="chip" style={{ "--r": `${(i % 3) - 1}deg` }}>{c}</span>)}
  </div>
);

export const Formulas = ({ items }) => (
  <div className="grid sm:grid-cols-2 gap-5 my-8">
    {items.map((f, i) => (
      <motion.div key={f.name} className="formula-card" whileHover={{ y: -4, rotate: i % 2 ? 0.6 : -0.6 }} data-testid={`formula-${f.name}`}>
        <div className="flex items-baseline justify-between gap-2">
          <span className="font-display text-4xl text-[#E4572E]">{f.name}</span>
          <span className="text-xs uppercase tracking-[0.18em] text-[#4A4A55]">{f.full}</span>
        </div>
        <p className="font-mono text-[13px] mt-3 bg-[#FBF7EE] px-3 py-2 rounded">{f.formula}</p>
        <p className="font-hand text-lg mt-3 text-[#2B59C3]">{f.example}</p>
      </motion.div>
    ))}
  </div>
);

export const NoteTable = ({ table, testId }) => (
  <div className="my-8 overflow-x-auto -mx-1 px-1" data-testid={testId}>
    <table className="note-table">
      <thead><tr>{table.head.map((h) => <th key={h}>{h}</th>)}</tr></thead>
      <tbody>{table.rows.map((r) => <tr key={r.join()}>{r.map((c, i) => <td key={i}>{c}</td>)}</tr>)}</tbody>
    </table>
  </div>
);

export const Playbooks = ({ items }) => (
  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 my-8">
    {items.map((p, i) => (
      <Sticky key={p.name} color={p.color} rotate={i % 2 ? 0.8 : -0.8} testId={`playbook-${i}`}>
        <p className="font-hand text-2xl mb-2">{p.name}</p>
        <ul className="space-y-1.5 text-[14px]">{p.lines.map((l) => <li key={l}>• {l}</li>)}</ul>
      </Sticky>
    ))}
  </div>
);
